import React, { Component, ReactNode, KeyboardEvent } from 'react';
import {
	DeviceType,
	Platform,
	Orientation,
	ShoppingGalleryItem,
	PredefinedConfiguration,
	ProductCardItem,
	SofaRangeName,
} from 'sofa-reborn-react';

import { KompisProductCard, KompisFocusOutline } from '@inter-ikea-kompis/react-components';
import ProductCardModalEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardModalEnum';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import ProductCardLayoutEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardLayoutEnum';
import ProductCardMediaEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardMediaEnum';
import ProductCardSelectBehaviourEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardSelectBehaviourEnum';
import { ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities';
import Card from '@ingka/card';

import { ProductViewWrapper } from './ProductViewStyle';
import { localeService } from '../../../services';
import { CardContainer } from '../ProductIntroGalleryStyle';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';

type Props = {
	platform: Platform;
	deviceType: DeviceType;
	orientation: Orientation;
	kompisSettings?: IDexfSettings | null;
	kompisTranslations?: ITranslations | null;
	galleryItems: ShoppingGalleryItem[];
	predefinedConfigurations?: PredefinedConfiguration[];
	startFromScratchImageSrc: string;
	startFromScratchElementIndex: number;
	nrPerRow: number;
	nrOfEmptyElements: number;
	visibleModal: ProductCardModalEnum | null;
	useLocalIds: boolean;
	ecoFeeSheetVisibleId: string | null;
	showCoverOnlyCard: boolean;
	isProduction: boolean;
	sofaRangeName?: SofaRangeName;
	onModalOpen?: (e: CustomEvent, productId: string) => void;
	onModalClose?: () => void;
	onItemClick?: (item: ProductCardItem, showCoverPicker: boolean) => void;
	onStartFromScratchClick?: () => void;
	onCoverOnlyClick?: () => void;
};

/**
 * React component.
 *
 */
export default class ProductView extends Component<Props> {
	/**
	 * React render method.
	 *
	 * @returns React node.
	 */
	public render(): ReactNode {
		const { props } = this;
		const { onItemClick, onStartFromScratchClick, onCoverOnlyClick, onModalOpen, onModalClose } =
			props;

		const galleryItemElements = props.galleryItems.map((galleryItem) => {
			const productVariants = props.predefinedConfigurations
				? props.predefinedConfigurations.find(
						(configuration) =>
							configuration.id === galleryItem.shoppingProduct.product.content?.itemNoGlobal ||
							configuration.id === galleryItem.shoppingProduct.product.content?.itemNoLocal
				  )?.productVariants
				: [];

			return (
				galleryItem.shoppingProduct.product.content &&
				props.kompisTranslations &&
				props.kompisSettings && (
					<div
						className="productViewItem"
						key={galleryItem.shoppingProduct.product.content.itemNoGlobal}
					>
						<KompisProductCard
							theme={SkapaTheme}
							settings={props.kompisSettings}
							translations={props.kompisTranslations.kompis}
							product={galleryItem.shoppingProduct.product}
							functionIcons={galleryItem.functionIcons}
							layout={ProductCardLayoutEnum.gallery}
							media={ProductCardMediaEnum.switchImageOnHover}
							productMeasures={ProductDescriptionProductMeasureEnum.short}
							selectBehaviour={ProductCardSelectBehaviourEnum.selectable}
							disableLinks={props.platform === 'kiosk'}
							storeAvailability={galleryItem.storeAvailability}
							showProductVariants={productVariants ? productVariants?.length > 0 : false}
							productVariantsLimit={4}
							productVariants={productVariants}
							visibleModal={
								props.ecoFeeSheetVisibleId === galleryItem.shoppingProduct.product.itemId
									? props.visibleModal
									: null
							}
							analytics={{
								dev: !props.isProduction,
								locale: localeService.getLocale(),
							}}
							onModalClose={onModalClose}
							onModalOpen={(e: CustomEvent): void => {
								onModalOpen && onModalOpen(e, galleryItem.shoppingProduct.product.itemId);
							}}
							onSelect={(): void => {
								if (galleryItem.shoppingProduct.product.content) {
									const itemNo = props.useLocalIds
										? galleryItem.shoppingProduct.product.content.itemNoLocal
										: galleryItem.shoppingProduct.product.content.itemNoGlobal ||
										  galleryItem.shoppingProduct.product.content.itemNoLocal;
									onItemClick && onItemClick({ id: itemNo, type: 'select' }, false);
								}
							}}
							onProductVariantShowMoreClick={(): void => {
								if (galleryItem.shoppingProduct.product.content) {
									const itemNo = props.useLocalIds
										? galleryItem.shoppingProduct.product.content.itemNoLocal
										: galleryItem.shoppingProduct.product.content.itemNoGlobal ||
										  galleryItem.shoppingProduct.product.content.itemNoLocal;
									onItemClick && onItemClick({ id: itemNo, type: 'showMore' }, true);
								}
							}}
							onProductVariantClick={(event: CustomEvent): void => {
								if (event.detail.product.content) {
									const itemNo = props.useLocalIds
										? event.detail.product.content.itemNoLocal
										: event.detail.product.content.itemNoGlobal ||
										  event.detail.product.content.itemNoLocal;
									onItemClick && onItemClick({ id: itemNo, type: 'variant' }, false);
								}
							}}
							// @ts-expect-error Missmatch kompis types
							role="button"
						/>
					</div>
				)
			);
		});

		galleryItemElements.splice(
			props.startFromScratchElementIndex,
			0,
			<div
				key="blank"
				className="productViewItem"
				onClick={onStartFromScratchClick}
				onKeyDown={(e: KeyboardEvent): void => {
					if (e.key === 'Enter' || e.key === ' ') {
						onStartFromScratchClick && onStartFromScratchClick();
					}
				}}
				role="button"
			>
				<KompisFocusOutline
					theme={SkapaTheme}
					// @ts-expect-error Missmatch kompis types
					tabIndex={0}
					style={{ height: '100%', width: '100%' }}
				>
					<CardContainer>
						<Card
							label={props.sofaRangeName || ''}
							backgroundColor="#C1D8D7"
							buttonVariant="icon-primary-inverse"
							compact
							title={localeService.getTranslation('startFromScratch')}
							emphasised
							imageProps={{
								src: props.startFromScratchImageSrc,
							}}
						/>
					</CardContainer>
				</KompisFocusOutline>
			</div>
		);
		if (props.showCoverOnlyCard) {
			galleryItemElements.splice(
				props.startFromScratchElementIndex + 7,
				0,
				<div
					key="cover"
					className="productViewItem"
					onClick={onCoverOnlyClick}
					onKeyDown={(event: KeyboardEvent): void => {
						if (event.key === 'Enter' || event.key === ' ') {
							onCoverOnlyClick && onCoverOnlyClick();
						}
					}}
					role="button"
				>
					<KompisFocusOutline
						theme={SkapaTheme}
						// @ts-expect-error Missmatch kompis types
						tabIndex={0}
						style={{ height: '100%', width: '100%' }}
					>
						<CardContainer>
							<Card
								label={props.sofaRangeName || ''}
								backgroundColor="rgb(var(--colour-neutral-2, 245, 245, 245))"
								buttonVariant="icon-primary-inverse"
								compact
								title={localeService.getTranslation('coverOnlyTitle')}
								emphasised
								imageProps={{
									src: 'https://www.ikea.com/se/sv/images/products/vimle-klaedsel-till-armstoed-hallarp-beige__0826813_pe776411_s5.jpg?f=xl',
								}}
							/>
						</CardContainer>
					</KompisFocusOutline>
				</div>
			);
		}

		/* Adding empty elements to fill up the last row to get the in between border correct */
		for (let i = 0; i < props.nrOfEmptyElements; i++) {
			galleryItemElements.push(<div className="productViewItem" key={i}></div>);
		}

		return (
			<ProductViewWrapper
				platform={props.platform}
				deviceType={props.deviceType}
				orientation={props.orientation}
				nrPerRow={props.nrPerRow}
			>
				{galleryItemElements}
			</ProductViewWrapper>
		);
	}
}
